import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { CalendarDays, ArrowRight, Check, Clock, Lock, Sparkles, Download, X, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

const sheetCls = "max-sm:!top-auto max-sm:!bottom-0 max-sm:!translate-y-0 max-sm:!max-w-full max-sm:!rounded-b-none max-sm:!rounded-t-2xl max-sm:max-h-[92vh] max-sm:overflow-y-auto rounded-xl sm:max-w-md";

const WHATSAPP = "918839790836";
const YEARLY_PRICE = "₹10,080/year";

function daysLeft(end) {
  if (!end) return null;
  const ms = new Date(end) - new Date();
  return Math.max(0, Math.ceil(ms / 86400000));
}

function A2HSPopup() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);
  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile || localStorage.getItem("pulse_a2hs_seen")) return;
    const t = setTimeout(() => { setShow(true); localStorage.setItem("pulse_a2hs_seen", "1"); }, 900);
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
  }, []);
  const close = () => { localStorage.setItem("pulse_a2hs_seen", "1"); setShow(false); };
  const install = async () => { if (deferred) { deferred.prompt(); await deferred.userChoice; } close(); };
  return (
    <Dialog open={show} onOpenChange={(v) => !v && close()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl" data-testid="a2hs-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-pulse-navy"><Download size={20} className="text-pulse-green" /> Add Pulse to your Home Screen</DialogTitle>
          <DialogDescription className="text-slate">Open your calendar in one tap, like a native app. Runs full-screen, works offline-friendly.</DialogDescription>
        </DialogHeader>
        <div className="mt-2 rounded-md bg-cloud p-4 text-sm text-charcoal">
          <p className="font-medium">On iPhone (Safari):</p>
          <p className="mt-1 text-slate">Tap the <b>Share</b> icon → <b>Add to Home Screen</b>.</p>
          <p className="mt-3 font-medium">On Android (Chrome):</p>
          <p className="mt-1 text-slate">Tap the <b>⋮</b> menu → <b>Add to Home screen</b>, or use the button below.</p>
        </div>
        <div className="mt-4 flex gap-2">
          {deferred && <button onClick={install} data-testid="a2hs-install" className="flex-1 rounded-md bg-pulse-green px-4 py-3 text-sm font-semibold text-white">Add now</button>}
          <button onClick={close} data-testid="a2hs-dismiss" className="flex-1 rounded-md border border-slate-200 px-4 py-3 text-sm font-semibold text-slate">Maybe later</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrialStartModal({ open, onClose, defaultEmail, onStarted }) {
  const [form, setForm] = useState({ business_name: "", venues: "", whatsapp: "", email: defaultEmail || "" });
  const [saving, setSaving] = useState(false);
  const [intendedVenues, setIntendedVenues] = useState(1);
  useEffect(() => { setForm((f) => ({ ...f, email: defaultEmail || "" })); }, [defaultEmail]);
  useEffect(() => {
    if (!open) return;
    let n = 1;
    try { n = Math.max(1, parseInt(localStorage.getItem("pulse_intended_venues") || "1", 10) || 1); } catch (e) {}
    setIntendedVenues(n);
    if (n > 1) {
      setForm((f) => (f.venues.trim() ? f : { ...f, venues: Array.from({ length: n }, (_, i) => `Venue ${i + 1}`).join(", ") }));
    }
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.whatsapp.trim() || !form.email.trim() || !form.venues.trim()) {
      toast.error("Please fill at least one venue, Gmail and WhatsApp number.");
      return;
    }
    if (!/^[^@\s]+@gmail\.com$/i.test(form.email.trim())) { toast.error("Please use a valid @gmail.com address."); return; }
    setSaving(true);
    try {
      await api.post("/trial/start", {
        business_name: form.business_name.trim(),
        venues: form.venues.split(",").map((v) => v.trim()).filter(Boolean),
        whatsapp: form.whatsapp.trim(), email: form.email.trim(),
      });
      toast.success("Your 15-day free trial has started!");
      try { localStorage.removeItem("pulse_intended_venues"); localStorage.removeItem("pulse_intended_billing"); } catch (e) {}
      onStarted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start trial.");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={sheetCls} data-testid="trial-start-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-pulse-navy">Start your free trial</DialogTitle>
          <DialogDescription className="text-slate">15 days free. Tell us about your business and we'll set up your calendar instantly.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-1 space-y-4">
          <div>
            <label className="text-sm font-medium text-charcoal">Business name <span className="text-slate">(optional)</span></label>
            <input data-testid="ts-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="mt-1.5 w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-pulse-green" placeholder="Nirvana Farms" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">Venue name(s) <span className="text-slate">— comma separated</span></label>
            {intendedVenues > 1 && (
              <p className="mt-0.5 text-xs text-pulse-green" data-testid="ts-venues-hint">Pre-filled {intendedVenues} venues from your Pricing selection — rename them below.</p>
            )}
            <input data-testid="ts-venues" value={form.venues} onChange={(e) => setForm({ ...form, venues: e.target.value })}
              className="mt-1.5 w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-pulse-green" placeholder="Nirvana, Niyara" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">Gmail address</label>
            <input type="email" data-testid="ts-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-pulse-green" placeholder="you@gmail.com" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">WhatsApp number</label>
            <input data-testid="ts-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              className="mt-1.5 w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-pulse-green" placeholder="+91 98765 43210" />
          </div>
          <button type="submit" disabled={saving} data-testid="ts-submit"
            className="w-full rounded-md bg-pulse-green px-6 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60">
            {saving ? "Starting…" : "Start 15-day free trial"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductsHub() {
  const { user } = useAuth();
  const { businessId } = useDash();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [trialOpen, setTrialOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["products", businessId],
    queryFn: async () => (await api.get("/products", { params: businessId ? { business_id: businessId } : {} })).data,
  });

  const cal = data?.products?.[0];
  const defaultEmail = user?.email && /@gmail\.com$/i.test(user.email) ? user.email : "";
  const left = cal?.state === "trial" ? daysLeft(cal.trial_end) : null;
  const buyHref = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi Pulse, my free trial has ended. I'd like to continue with the Calendar (${YEARLY_PRICE}).`)}`;

  const onStarted = () => {
    qc.invalidateQueries();
    setTrialOpen(false);
    navigate("/dashboard/calendar");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Your products</h1>
        <p className="text-sm text-slate">Everything you've unlocked, plus what's coming to Pulse.</p>
      </div>

      {cal?.state === "trial" && left !== null && (
        <div className="flex items-center gap-3 rounded-xl border border-pulse-green/30 bg-pulse-green/5 px-5 py-3.5" data-testid="trial-banner">
          <Clock size={18} className="text-pulse-green" />
          <span className="text-sm font-medium text-pulse-navy">Your free trial is in progress — <b>{left} day{left === 1 ? "" : "s"}</b> left. Enjoy full access to the Calendar.</span>
        </div>
      )}

      {cal?.state === "expired" && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-5 py-3.5" data-testid="expired-banner">
          <Lock size={18} className="shrink-0 text-red-500" />
          <span className="text-sm font-medium text-red-700">Your free trial has ended. Your calendar and all your data are safely saved — buy Pulse to unlock everything again.</span>
        </div>
      )}

      {isLoading ? <Skeleton className="h-64 w-full rounded-xl" /> : (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Calendar product */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-pulse-md" data-testid="product-calendar">
            <div className="relative h-40 overflow-hidden bg-pulse-navy">
              <img src={cal?.image} alt="Calendar" className="h-full w-full object-cover opacity-90" />
              {cal?.owned && (
                <span className={`absolute right-3 top-3 rounded-md px-2.5 py-1 text-xs font-semibold text-white capitalize ${cal.state === "expired" ? "bg-red-500" : "bg-pulse-green"}`}>
                  {cal.state === "trial" ? "Trial" : cal.state === "active" ? "Active" : cal.state === "expired" ? "Trial ended" : cal.state}
                </span>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2"><CalendarDays size={18} className="text-pulse-green" /><h3 className="text-xl font-semibold text-pulse-navy">Calendar</h3></div>
              <p className="mt-2 text-sm text-slate">{cal?.description}</p>
              {cal?.state === "trial" || cal?.state === "active" ? (
                <button onClick={() => navigate("/dashboard/calendar")} data-testid="open-calendar-btn"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-pulse-navy px-5 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]">
                  View your calendar <ArrowRight size={16} />
                </button>
              ) : cal?.state === "expired" ? (
                <a href={buyHref} target="_blank" rel="noopener noreferrer" data-testid="buy-now-btn"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-pulse-navy px-5 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]">
                  <ShoppingCart size={16} /> Buy now · {YEARLY_PRICE}
                </a>
              ) : (
                <button onClick={() => setTrialOpen(true)} data-testid="start-trial-btn"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-pulse-green px-5 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]">
                  Start 15-day free trial <ArrowRight size={16} />
                </button>
              )}
            </div>
          </motion.div>

          {/* Coming soon */}
          {Array.from({ length: data?.coming_soon || 2 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-cloud/40 p-8 text-center" data-testid="product-coming-soon">
              <div className="grid h-11 w-11 place-items-center rounded-md bg-white shadow-pulse-sm"><Sparkles size={20} className="text-pulse-green" /></div>
              <h3 className="mt-4 text-lg font-semibold text-slate">New product</h3>
              <p className="mt-1 text-sm text-slate/70">More tools for your business are on the way.</p>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate"><Lock size={12} /> Coming soon</span>
            </div>
          ))}
        </div>
      )}

      <TrialStartModal open={trialOpen} onClose={() => setTrialOpen(false)} defaultEmail={defaultEmail} onStarted={onStarted} />
      <A2HSPopup />
    </div>
  );
}
